import {apiClient} from '@common/http/query-client';

export interface NativePushDeviceInfo {
  provider?: 'onesignal' | string;
  platform?: 'android' | 'ios' | string;
  subscription_id?: string | null;
  device_uuid?: string | null;
  app_version?: string | null;
  enabled?: boolean;
  notification_permission?: string;
}

interface NativePushBridge {
  getDeviceInfo?: () =>
    | string
    | NativePushDeviceInfo
    | Promise<string | NativePushDeviceInfo>;
  login?: (userId: string | number) => void;
  logout?: () => void;
  refresh?: () => string | NativePushDeviceInfo | undefined;
}

declare global {
  interface Window {
    NativePush?: NativePushBridge;
  }
}

export async function getNativePushDeviceInfo(): Promise<NativePushDeviceInfo | null> {
  const bridge = nativePushBridge();
  if (!bridge?.getDeviceInfo) {
    return null;
  }

  try {
    const rawInfo = await bridge.getDeviceInfo();
    return parseDeviceInfo(rawInfo);
  } catch {
    return null;
  }
}

export function scheduleNativePushSync(userId?: number | string | null): void {
  window.setTimeout(() => {
    syncNativePushDevice(userId).catch(() => {});
  }, 500);
}

export async function syncNativePushDevice(
  userId?: number | string | null,
): Promise<void> {
  const bridge = nativePushBridge();
  if (!bridge) {
    return;
  }

  if (userId != null) {
    bridge.login?.(userId);
  }

  const info = await getNativePushDeviceInfoWithRetry();
  if (!isRegistrable(info)) {
    return;
  }

  await apiClient.post('me/push-devices', {
    provider: info.provider,
    platform: info.platform,
    subscription_id: info.subscription_id,
    device_uuid: info.device_uuid,
    app_version: info.app_version,
    enabled: info.enabled ?? true,
    notification_permission: info.notification_permission,
  });
}

export async function unregisterNativePushDevice(): Promise<void> {
  const info = await getNativePushDeviceInfo();
  try {
    if (info?.device_uuid) {
      await apiClient.delete(
        `me/push-devices/${encodeURIComponent(info.device_uuid)}`,
      );
    }
  } finally {
    nativePushBridge()?.logout?.();
  }
}

function nativePushBridge(): NativePushBridge | null {
  return typeof window === 'undefined' ? null : window.NativePush || null;
}

async function getNativePushDeviceInfoWithRetry(): Promise<NativePushDeviceInfo | null> {
  let latestInfo: NativePushDeviceInfo | null = null;

  for (let attempt = 0; attempt < 5; attempt++) {
    nativePushBridge()?.refresh?.();
    latestInfo = await getNativePushDeviceInfo();
    if (latestInfo?.subscription_id) {
      return latestInfo;
    }
    await wait(1000);
  }

  return latestInfo;
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function parseDeviceInfo(
  rawInfo: string | NativePushDeviceInfo | undefined,
): NativePushDeviceInfo | null {
  if (!rawInfo) {
    return null;
  }
  if (typeof rawInfo === 'string') {
    return JSON.parse(rawInfo) as NativePushDeviceInfo;
  }
  return rawInfo;
}

function isRegistrable(
  info: NativePushDeviceInfo | null,
): info is NativePushDeviceInfo {
  return !!(
    info?.provider &&
    info.platform &&
    info.device_uuid
  );
}
